"use client";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import Breadcrumb from './Breadcrumb';
import ProductGrid from './ProductGrid';
import { useWishlist } from '../../context/WishlistContext'; // ✅ Import Wishlist context

const Wishlist = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { wishlist, removeFromWishlist } = useWishlist(); // ✅ Access wishlist & remove function
  
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  // Pagination setup
  const itemsPerPage = 8;
  const totalPages = Math.ceil(wishlist.length / itemsPerPage);

  const paginatedProducts = wishlist.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // ✅ Remove item handler
  const handleRemove = (productId) => {
    removeFromWishlist(productId);

    // If current page becomes empty after removal, go to previous page
    if ((paginatedProducts.length === 1) && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div>
      <Header />

      <main className="flex gap-7 p-10 w-full min-h-screen bg-neutral-50 max-xl:flex-col max-xl:gap-5 max-md:p-5 max-sm:p-4">
        <section className="flex flex-col flex-1 gap-5 min-w-0">
          <Breadcrumb />

          <header className="flex justify-between items-center max-sm:flex-col max-sm:items-start max-sm:gap-2">
            <h1 className="text-2xl font-bold leading-6 text-black">Wishlist</h1>
            <p className="text-lg leading-6 text-black">
              {wishlist.length} item{wishlist.length !== 1 && 's'}
            </p>
          </header>

          {wishlist.length === 0 ? (
            <p className="text-center text-gray-500 mt-10">Your wishlist is empty.</p>
          ) : (
            <ProductGrid
              products={paginatedProducts}
              onProductClick={handleProductClick}
              onRemoveFromWishlist={handleRemove} // ✅ Pass remove handler
            />
          )}

          {wishlist.length > itemsPerPage && (
            <nav className="flex gap-2.5 justify-center items-center max-sm:flex-wrap max-sm:gap-1.5" aria-label="Pagination Navigation">
              <button
                className="p-2.5 text-base text-black cursor-pointer hover:text-gray-600 rounded max-sm:p-2 max-sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="flex gap-2.5 items-center max-sm:gap-1.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`p-2.5 text-base font-semibold rounded-md cursor-pointer max-sm:p-2 max-sm:text-sm transition-colors duration-200 ${
                      currentPage === page
                        ? 'bg-sky-950 text-white'
                        : 'text-black hover:bg-gray-100'
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="p-2.5 text-base font-bold text-black cursor-pointer hover:text-gray-600 rounded max-sm:p-2 max-sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </nav>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Wishlist;
